import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'forestgis.theme';

/** Tema padrão da identidade EyeForest v3 (docs/spec-identidade-visual-v3.md, §19). */
export const DEFAULT_THEME: Theme = 'light';

/**
 * Só os mapas públicos (link compartilhado e mapa público) são sempre escuros. O mapa do projeto acompanha o
 * tema desde a fase 1 do mapa (docs/spec-identidade-visual-v3.md, §24); o mapa base não depende do tema.
 */
export function isDarkOnlyRoute(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return /^\/(shared-link|public-map)\//.test(path);
}

const isTheme = (value: unknown): value is Theme => value === 'light' || value === 'dark';

/**
 * Tema claro/escuro do app. Aplica a classe `.dark` no <html> (os tokens de `_theme-v3.scss` trocam sozinhos),
 * `color-scheme` (controles nativos e barras de rolagem) e guarda a escolha no localStorage.
 *
 * O localStorage pode lançar (modo privado, cota, site data bloqueado): leitura e escrita são protegidas e o
 * tema é aplicado mesmo sem persistência. Nas rotas de mapa, `.dark` é forçado sem mexer na preferência salva.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  private readonly router = inject(Router);

  /** Preferência do usuário (a que o botão alterna e o localStorage guarda). */
  readonly theme = signal<Theme>(DEFAULT_THEME);
  /** Verdadeiro nas rotas de mapa: o tema aplicado é escuro, seja qual for a preferência. */
  readonly forcedDark = signal(false);
  /** O tema de fato aplicado (escuro nos mapas públicos, senão a preferência): para quem precisa acompanhar o app. */
  readonly effective = computed<Theme>(() => (this.forcedDark() ? 'dark' : this.theme()));

  constructor() {
    this.theme.set(this.read());
    this.forcedDark.set(isDarkOnlyRoute(this.router.url));
    this.render();
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;
      const forced = isDarkOnlyRoute(event.urlAfterRedirects);
      if (forced === this.forcedDark()) return;
      this.forcedDark.set(forced);
      this.render();
    });
  }

  set(theme: Theme): void {
    this.theme.set(theme);
    this.render();
    this.persist(theme);
  }

  toggle(): void {
    if (this.forcedDark()) return;
    this.set(this.theme() === 'dark' ? 'light' : 'dark');
  }

  private render(): void {
    const applied = this.effective();
    const root = this.document.documentElement;
    root.classList.toggle('dark', applied === 'dark');
    root.style.colorScheme = applied;
  }

  private read(): Theme {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);
      return isTheme(stored) ? stored : DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  }

  private persist(theme: Theme): void {
    try {
      this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Sem persistência (modo privado/cota): o tema já foi aplicado nesta sessão.
    }
  }
}
